import React, { useEffect } from 'react';

import { isEmpty } from 'lodash-es';
import Scripts from 'src/Scripts';

import { NeuApp } from '@hcahealthcaredpx/neutron-react';
import {
  ComponentFields,
  ComponentRendering,
  HTMLLink,
  LayoutServiceData,
  Placeholder,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { CookieConsent } from '@xsf/shared-lib/components/cookie-consent';
import { useHeaderContext } from '@xsf/shared-lib/components/header';
import { Invoca } from '@xsf/shared-lib/components/invoca';
import { useInert } from '@xsf/shared-lib/hooks/utils';

import { UseMyLocationFormProvider } from './providers/use-my-location-form-provider';
import { LayoutServiceContext } from './types/SitecoreLayout';
import { SitecoreTheme } from './types/SitecoreTheme';

type LayoutData = LayoutServiceData & {
  sitecore: {
    context: LayoutServiceContext & {
      theme?: SitecoreTheme;
    };
  };
};
interface LayoutProps {
  layoutData: LayoutData;
  headLinks: HTMLLink[];
  scSiteCookie: string | undefined;
}

// Prefix public assets with a public URL to enable compatibility with Sitecore Experience Editor.
// If you're not supporting the Experience Editor, you can remove this.

const Layout = ({ layoutData, scSiteCookie }: LayoutProps): JSX.Element => {
  const { route } = layoutData.sitecore;
  //const fields = route?.fields as RouteFields;
  const isPageEditing = layoutData.sitecore.context.pageEditing;
  const mainClassPageEditing = isPageEditing ? 'editing-mode' : 'prod-mode';

  const header = layoutData?.sitecore?.route?.placeholders?.header?.[0];
  const footer = layoutData?.sitecore?.route?.placeholders?.footer?.[0];

  const themeId = layoutData.sitecore.context.theme?.id || '';

  const { mobileMenuOpen } = useHeaderContext();

  const { ref: contentRef } = useInert(mobileMenuOpen);
  const { ref: footerRef } = useInert(mobileMenuOpen);

  useEffect(() => {
    // We need OAS_IFRAME_HEIGHT in order to adjust the iframe height when load the module into MHO
    if (!scSiteCookie || scSiteCookie !== 'MyHealthOne') return;

    const sendHeight = (): void => {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: 'OAS_IFRAME_HEIGHT', height }, '*');
    };
    sendHeight();

    const observer = new MutationObserver(sendHeight);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [scSiteCookie]);

  //const title = fields?.Title?.value ?? fields['Browser Title']?.value;
  return (
    <>
      <CookieConsent />
      <Invoca />
      {route && <Placeholder name="head" rendering={route} />}
      <Scripts />
      {/* root placeholder for the app, which we add components to using route data */}
      <div className={mainClassPageEditing} data-theme={themeId.toUpperCase().replace(/-/g, '')}>
        <NeuApp>
          {route && !isEmpty((header as ComponentRendering<ComponentFields>)?.fields) && (
            <Placeholder name="header" rendering={route} />
          )}
          <div ref={contentRef}>
            <NeuApp.Content>
              <UseMyLocationFormProvider>
                {route && <Placeholder name="body" rendering={route} />}
              </UseMyLocationFormProvider>
            </NeuApp.Content>
          </div>
          <div ref={footerRef}>
            <NeuApp.Footer>
              {route && !isEmpty((footer as ComponentRendering<ComponentFields>)?.fields) && (
                <Placeholder name="footer" rendering={route} />
              )}
            </NeuApp.Footer>
          </div>
        </NeuApp>
      </div>
    </>
  );
};

export default Layout;
